import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Archive, Download, Eye, Heart, ImageOff, Loader2, Mail, Monitor, RefreshCw, ScanFace, Smartphone, Tablet, UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { StatusPill } from '../cockpit/Cockpit';
import {
  deliveredSizeLabel, formatDate, formatDateTime, formatRelative, initials, linkLabel, viewerTimeZone,
} from './analyticsFormat';

const DEVICE_ICONS = { mobile: Smartphone, tablet: Tablet, desktop: Monitor };
const MATCH_THUMBS_SHOWN = 12;

const ZIP_STATUS = {
  completed: { tone: 'success', label: 'Ready' },
  processing: { tone: 'warning', label: 'Processing' },
  failed: { tone: 'danger', label: 'Failed' },
  pending: { tone: 'neutral', label: 'Queued' },
};

const DeliveryPill = ({ branded }) => (
  <StatusPill tone={branded ? 'scheduled' : 'neutral'}>{branded ? 'Branded' : 'Original'}</StatusPill>
);

/** The delivered file's thumbnail, falling back to the plain photo if it's missing. */
const DeliveredImage = ({ fileUrl, photoUrl, alt, className }) => {
  const [src, setSrc] = useState(fileUrl || photoUrl);
  useEffect(() => setSrc(fileUrl || photoUrl), [fileUrl, photoUrl]);
  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-400 ${className}`}>
        <ImageOff className="w-5 h-5" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`object-contain bg-slate-100 dark:bg-slate-800 ${className}`}
      onError={() => setSrc(src === fileUrl && photoUrl && photoUrl !== fileUrl ? photoUrl : null)}
    />
  );
};

const Stat = ({ label, value, detail }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2.5">
    <div className="tac-kicker text-[10px] text-slate-400">{label}</div>
    <div className="text-xl font-semibold text-slate-900 dark:text-white mt-0.5">{value}</div>
    {detail && <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{detail}</div>}
  </div>
);

const SearchResult = ({ item, galleryId, backendUrl, axiosConfig }) => {
  const [rematch, setRematch] = useState(null);
  const [running, setRunning] = useState(false);

  const runRematch = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/events/${item.id}/rematch`, {}, axiosConfig);
      setRematch(data.matched_photos || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not re-run the search');
    } finally {
      setRunning(false);
    }
  };

  const photos = rematch || item.matched_photos || [];
  const count = rematch ? rematch.length : item.match_count;
  const summary = count === null || count === undefined
    ? 'Result not recorded'
    : count === 0 ? 'No matching photos' : `Found ${count} photo${count === 1 ? '' : 's'}`;

  return (
    <div className="flex gap-3">
      {item.selfie_url ? (
        <img src={item.selfie_url} alt="Search selfie" className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-slate-200 dark:border-slate-700" />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
          <ScanFace className="w-5 h-5 text-slate-400" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm text-slate-800 dark:text-slate-100">
          Face search · <span className={count ? 'font-medium' : 'text-slate-500'}>{summary}</span>
          {rematch && <span className="text-xs text-slate-500"> (re-run now)</span>}
        </div>
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {photos.slice(0, MATCH_THUMBS_SHOWN).map((p) => (
              <img
                key={p.photo_id}
                src={p.thumbnail_url}
                alt=""
                loading="lazy"
                title={p.similarity ? `${Math.round(p.similarity)}% match` : undefined}
                className="w-10 h-10 rounded object-cover border border-slate-200 dark:border-slate-700"
              />
            ))}
            {photos.length > MATCH_THUMBS_SHOWN && (
              <span className="w-10 h-10 rounded bg-slate-100 dark:bg-slate-800 text-xs text-slate-500 flex items-center justify-center">
                +{photos.length - MATCH_THUMBS_SHOWN}
              </span>
            )}
          </div>
        )}
        {item.can_rematch && (
          <button
            type="button"
            onClick={runRematch}
            disabled={running}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 dark:hover:text-white disabled:opacity-50"
          >
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Re-run against the gallery now
          </button>
        )}
      </div>
    </div>
  );
};

const TimelineItem = ({ item, galleryId, backendUrl, axiosConfig }) => {
  const DeviceIcon = DEVICE_ICONS[item.device];
  let icon;
  let body;

  switch (item.type) {
    case 'visit':
      icon = Eye;
      body = (
        <div className="text-sm text-slate-700 dark:text-slate-200">
          Opened the {linkLabel(item.link)} link
          {item.count > 1 && <span className="text-slate-500"> · {item.count} times that day</span>}
        </div>
      );
      break;
    case 'identify':
      icon = UserCheck;
      body = <div className="text-sm text-slate-700 dark:text-slate-200">Entered their details</div>;
      break;
    case 'save':
      icon = Smartphone;
      body = (
        <div className="text-sm text-slate-700 dark:text-slate-200">
          {item.outcome === 'shared'
            ? <>Completed the iPhone share sheet <span className="text-slate-500">(usually Save Image)</span></>
            : <span className="text-slate-500">Closed the iPhone share sheet without saving</span>}
          {item.filename && <span className="text-slate-500"> · {item.filename}</span>}
        </div>
      );
      break;
    case 'face_match_email':
      icon = Mail;
      body = (
        <div>
          <div className="text-sm text-slate-700 dark:text-slate-200">
            Emailed about <span className="font-medium">{item.match_count} new photo{item.match_count === 1 ? '' : 's'}</span> of them
            {item.total_count > item.match_count && <span className="text-slate-500"> ({item.total_count} in total)</span>}
          </div>
          {item.matched_photos?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {item.matched_photos.slice(0, MATCH_THUMBS_SHOWN).map((p) => (
                <img key={p.photo_id} src={p.thumbnail_url} alt="" loading="lazy"
                  className="w-10 h-10 rounded object-cover border border-slate-200 dark:border-slate-700" />
              ))}
            </div>
          )}
        </div>
      );
      break;
    case 'face_search':
      icon = ScanFace;
      body = <SearchResult item={item} galleryId={galleryId} backendUrl={backendUrl} axiosConfig={axiosConfig} />;
      break;
    case 'download': {
      icon = Download;
      const size = deliveredSizeLabel(item);
      body = (
        <div className="flex gap-3 items-center">
          <DeliveredImage
            fileUrl={item.file_thumb_url}
            photoUrl={item.photo_thumb_url}
            alt={item.filename || 'Downloaded photo'}
            className="w-14 h-12 rounded flex-shrink-0 border border-slate-200 dark:border-slate-700"
          />
          <div className="min-w-0">
            <div className="text-sm text-slate-800 dark:text-slate-100 truncate">{item.filename || 'Photo'}</div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <DeliveryPill branded={item.branded} />
              {size && <span className="text-xs text-slate-500">{size}</span>}
              {item.photo_deleted && <span className="text-xs text-slate-400">photo since deleted</span>}
            </div>
          </div>
        </div>
      );
      break;
    }
    case 'zip_request': {
      icon = Archive;
      const status = ZIP_STATUS[item.status];
      body = (
        <div>
          <div className="text-sm text-slate-700 dark:text-slate-200">
            Requested a ZIP of {item.photo_count ?? '?'} photos
            {item.favorites_list_name ? <> from “{item.favorites_list_name}”</> : null}
          </div>
          <div className="flex items-center gap-2 mt-1">
            {status && <StatusPill tone={status.tone}>{status.label}</StatusPill>}
            {item.email_sent && <span className="text-xs text-slate-500">Link emailed</span>}
            {deliveredSizeLabel(item) && <span className="text-xs text-slate-500">{deliveredSizeLabel(item)}</span>}
          </div>
        </div>
      );
      break;
    }
    case 'favorites_list':
      icon = Heart;
      body = (
        <div className="text-sm text-slate-700 dark:text-slate-200">
          Created favourites list “{item.favorites_list_name || 'Untitled'}”
        </div>
      );
      break;
    default:
      return null;
  }

  const Icon = icon;
  return (
    <li className="flex gap-3 py-3">
      <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
        <Icon className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
      </div>
      <div className="min-w-0 flex-1">{body}</div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0 text-right">
        <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{formatDateTime(item.at)}</span>
        <span className="flex items-center gap-1 text-[11px] text-slate-400 whitespace-nowrap">
          {DeviceIcon && <DeviceIcon className="w-3 h-3" />}
          {item.type !== 'visit' && linkLabel(item.link)}
        </span>
      </div>
    </li>
  );
};

const PersonSheet = ({ email, onClose, galleryId, backendUrl, axiosConfig }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  // Props that are recreated on every parent render are read through refs, so they
  // never re-trigger the fetch.
  const configRef = useRef(axiosConfig);
  configRef.current = axiosConfig;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    setDetail(null);
    if (!email) return undefined;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${backendUrl}/api/galleries/${galleryId}/people/detail`, {
        ...configRef.current,
        params: { email, tz: viewerTimeZone() },
      })
      .then(({ data }) => { if (!cancelled) setDetail(data); })
      .catch((error) => {
        if (cancelled) return;
        toast.error(error.response?.data?.detail || 'Could not load this person');
        closeRef.current();
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [email, galleryId, backendUrl]);

  const person = detail?.person;
  const selfie = detail?.timeline.find((t) => t.type === 'face_search' && t.selfie_url)?.selfie_url;

  return (
    <Sheet open={!!email} onOpenChange={(open) => { if (!open) onClose(); }}>
      {/* Explicit surface: App.css redefines --background as a hex, which breaks bg-background. */}
      <SheetContent side="right" className="z-[10000] w-full sm:max-w-2xl p-0 overflow-y-auto bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        {!person ? (
          <div className="h-full flex items-center justify-center">
            <SheetTitle className="sr-only">Loading person</SheetTitle>
            {loading && <Loader2 className="w-6 h-6 animate-spin text-slate-400" />}
          </div>
        ) : (
          <>
            <SheetHeader className="p-5 pr-12 border-b border-slate-200 dark:border-slate-800 text-left">
              <div className="flex items-center gap-4">
                {selfie ? (
                  <img src={selfie} alt="" className="w-14 h-14 rounded-full object-cover border border-slate-200 dark:border-slate-700" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-slate-900 dark:bg-slate-700 text-white flex items-center justify-center font-semibold">
                    {initials(person.name, person.email)}
                  </div>
                )}
                <div className="min-w-0">
                  <SheetTitle className="text-lg truncate text-slate-900 dark:text-white">{person.name || person.email}</SheetTitle>
                  <SheetDescription className="truncate text-slate-500 dark:text-slate-400">
                    {person.email}{person.domain ? ` · ${person.domain}` : ''}
                  </SheetDescription>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {person.links.map((link) => <StatusPill key={link} tone="info">{linkLabel(link)}</StatusPill>)}
                    {person.subscribed && <StatusPill tone="success">Match emails on</StatusPill>}
                    <span className="text-xs text-slate-500">
                      First seen {formatDate(person.first_seen)} · last active {formatRelative(person.last_seen)}
                    </span>
                  </div>
                </div>
              </div>
            </SheetHeader>

            <div className="p-5 space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Visits" value={person.visits} />
                <Stat
                  label="Found in photos"
                  value={person.photos_found}
                  detail={`${person.searches} search${person.searches === 1 ? '' : 'es'} · ${person.match_emails} match email${person.match_emails === 1 ? '' : 's'}`}
                />
                <Stat
                  label="Downloads"
                  value={person.downloads}
                  detail={person.downloads ? `${person.branded_downloads} branded · ${person.original_downloads} original` : null}
                />
                <Stat
                  label="ZIP requests"
                  value={person.zip_requests}
                  detail={person.zip_requests ? `${person.zip_photos} photos` : null}
                />
              </div>

              <section>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                  Files downloaded
                  <span className="font-normal text-slate-500"> · {detail.downloaded_photos.length} different photo{detail.downloaded_photos.length === 1 ? '' : 's'}</span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                  Each preview is the file they were sent, with the frame if it was branded.
                </p>
                {detail.downloaded_photos.length === 0 ? (
                  <p className="text-sm text-slate-500">No single-photo downloads.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {detail.downloaded_photos.map((file) => {
                      const size = deliveredSizeLabel({ size: file.sizes[0], served_from: file.served_from });
                      return (
                        <figure key={`${file.photo_id}-${file.branded}-${file.file_thumb_url}`} className="min-w-0">
                          <div className="relative">
                            <DeliveredImage
                              fileUrl={file.file_thumb_url}
                              photoUrl={file.photo_thumb_url}
                              alt={file.filename || 'Downloaded photo'}
                              className="w-full aspect-[4/3] rounded-lg border border-slate-200 dark:border-slate-700"
                            />
                            <span className="absolute top-1.5 left-1.5"><DeliveryPill branded={file.branded} /></span>
                            {file.count > 1 && (
                              <span className="absolute top-1.5 right-1.5 rounded-full bg-slate-900/80 text-white text-[11px] font-semibold px-1.5 py-0.5">
                                ×{file.count}
                              </span>
                            )}
                          </div>
                          <figcaption className="mt-1.5">
                            <div className="text-xs font-medium text-slate-800 dark:text-slate-100 truncate" title={file.filename}>
                              {file.filename || 'Photo'}
                            </div>
                            <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                              {[size, file.frame_name].filter(Boolean).join(' · ') || '—'}
                            </div>
                            <div className="text-[11px] text-slate-400">
                              {formatDateTime(file.last_at)}
                              {file.frame_reconstructed && <span title="Downloaded before per-file tracking; the preview uses the frame this photo has today"> · frame as set today</span>}
                            </div>
                          </figcaption>
                        </figure>
                      );
                    })}
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Activity</h3>
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {detail.timeline.map((item, index) => (
                    <TimelineItem
                      key={item.id || `visit-${index}`}
                      item={item}
                      galleryId={galleryId}
                      backendUrl={backendUrl}
                      axiosConfig={axiosConfig}
                    />
                  ))}
                </ul>
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default PersonSheet;
